import React from 'react';
import { FiShield, FiTrendingUp, FiCheckCircle, FiFileText, FiZap, FiUsers, FiAward, FiBarChart2, FiGlobe } from 'react-icons/fi';

const APAARID = () => {
  const keyFeatures = [
    {
      icon: <FiGlobe className="w-6 h-6" />,
      title: 'One Nation, One Student ID',
      description: 'APAAR (Automated Permanent Academic Account Registry) is a 12-digit unique student identification number designed to serve as a lifelong academic passport for every student in India. It stays with the student from pre-primary education through higher education and skill development programs [citation:3][citation:7].',
      stat: '26.35 crore+ APAAR IDs have been generated across India',
      color: 'bg-indigo-50'
    },
    {
      icon: <FiFileText className="w-6 h-6" />,
      title: 'Digital Repository of Academic Records',
      description: 'APAAR acts as a central repository for all academic achievements. It stores marksheets, report cards, board certificates, degrees, diplomas, academic credits, scholarships, awards, co-curricular achievements, and skill certificates—all in one secure digital location [citation:7][citation:8].',
      stat: 'Reduces paperwork and eliminates risk of lost certificates',
      color: 'bg-emerald-50'
    },
    {
      icon: <FiShield className="w-6 h-6" />,
      title: 'Seamless Academic Mobility',
      description: 'APAAR enables students to seamlessly transition between institutions. When a student changes schools or moves from school to college, their records follow them effortlessly. No more chasing schools for transfer certificates or lost records [citation:1][citation:3].',
      stat: 'Facilitates Multiple Entry Multiple Exit (MEME) framework under NEP 2020',
      color: 'bg-amber-50'
    },
    {
      icon: <FiUsers className="w-6 h-6" />,
      title: 'Integration with DigiLocker and ABC',
      description: 'APAAR IDs are linked to students\' DigiLocker accounts, enabling secure storage and access to verified educational documents. It also integrates with the Academic Bank of Credits (ABC), allowing students to accumulate, transfer, and redeem academic credits across institutions [citation:2][citation:9].',
      stat: 'ABC supports credit accumulation, transfer, and redemption up to 7 years',
      color: 'bg-rose-50'
    },
    {
      icon: <FiCheckCircle className="w-6 h-6" />,
      title: 'Consent-Based Data Security',
      description: 'APAAR prioritizes data privacy and security. The enrolment is voluntary and consent-based. For minors, parental consent is required before creating an APAAR ID. Data is shared only with necessary government agencies, and students maintain control over who accesses their records [citation:3][citation:5].',
      stat: 'Compliant with Digital Personal Data Protection (DPDP) Act, 2023',
      color: 'bg-purple-50'
    },
    {
      icon: <FiTrendingUp className="w-6 h-6" />,
      title: 'Evidence-Based Decision Making for Institutions',
      description: 'APAAR enables schools to take evidence-based decisions by providing access to comprehensive student data. It streamlines operations, simplifies admissions, and organizes crucial information for enhanced academic management [citation:6].',
      stat: 'Helps institutions track academic progress and improve policy planning',
      color: 'bg-blue-50'
    }
  ];

  const managementSteps = [
    {
      title: 'Register Institution on NAD Portal',
      description: 'Every educational institution must first register on the National Academic Depository (NAD) portal. This registration enables the institution to get registered with the Academic Bank of Credits (ABC) portal, which is the backbone of APAAR management [citation:6].'
    },
    {
      title: 'Conduct Parent-Teacher Meetings',
      description: 'Schools should organize Parent-Teacher Meetings to introduce APAAR and explain its significance, benefits, and the consent process to parents. This ensures families understand the purpose of the ID and can make informed decisions [citation:6][citation:10].'
    },
    {
      title: 'Distribute and Collect Consent Forms',
      description: 'Schools must provide physical consent forms to parents. For minor students, parents need to fill, sign, and submit these forms to authorize the use of Aadhaar details for generating APAAR IDs. This is a critical step for voluntary, consent-based enrolment [citation:6][citation:10].'
    },
    {
      title: 'Verify Student Data on UDISE+ Portal',
      description: 'School authorities must authenticate student details—name, gender, DOB, parents\' names, and Aadhaar number—on the UDISE+ portal. Data should match exactly with school records and Aadhaar information to ensure successful creation [citation:6][citation:10].'
    },
    {
      title: 'Generate APAAR IDs',
      description: 'Using the UDISE+ APAAR module, school authorities generate APAAR IDs upon successful verification. The ID is then securely pushed to the student\'s DigiLocker account. A confirmation SMS is sent to parents through their registered mobile number linked to Aadhaar [citation:6][citation:9].'
    },
    {
      title: 'Integrate with Existing ERP',
      description: 'Institutions should integrate their existing ERP system with NAD for seamless data upload. This enables real-time upload of results and academic records directly to the ABC portal, with APAAR IDs linking everything together [citation:6].'
    }
  ];

  const benefits = [
    {
      title: 'Lifelong Academic Identity',
      description: 'A single ID that stays with the student throughout their entire academic journey, from school to higher education.',
      icon: <FiAward className="w-5 h-5" />
    },
    {
      title: 'No More Lost Certificates',
      description: 'All academic records are stored digitally and securely. No fear of losing hard copy certificates [citation:1].',
      icon: <FiShield className="w-5 h-5" />
    },
    {
      title: 'Faster Record Transfers',
      description: 'Records can be shared instantly when students transfer schools or apply for admissions, reducing processing time [citation:3].',
      icon: <FiZap className="w-5 h-5" />
    },
    {
      title: 'Reduced Paperwork',
      description: 'Institutions and students spend less time managing physical documents. Everything is digital and accessible [citation:1].',
      icon: <FiFileText className="w-5 h-5" />
    },
    {
      title: 'Better Policy Planning',
      description: 'Accurate educational data helps authorities monitor enrolment, reduce dropouts, and implement government schemes more effectively [citation:7].',
      icon: <FiBarChart2 className="w-5 h-5" />
    },
    {
      title: 'Simplified Verification',
      description: 'Schools, colleges, and employers can quickly verify academic records through the APAAR ID, saving time on manual verification [citation:2].',
      icon: <FiCheckCircle className="w-5 h-5" />
    }
  ];

  return (
    <>
      {/* Introduction */}
      <p className="text-lg text-slate-600 leading-relaxed mb-6">
        The Government of India has introduced APAAR (Automated Permanent Academic Account Registry) as a revolutionary initiative to create a unified student identity across the country. Part of the "One Nation, One Student ID" program under NEP 2020, APAAR assigns a unique 12-digit ID to every student that stays with them throughout their academic journey—from pre-primary school to higher education [citation:3][citation:7].
      </p>
      
      <p className="text-lg text-slate-600 leading-relaxed mb-6">
        For educational institutions, <strong className="text-slate-900">managing APAAR IDs is becoming a mandatory compliance requirement.</strong> Schools and colleges must generate APAAR IDs for all students, maintain accurate records, and integrate these IDs into their academic management systems [citation:4][citation:10].
      </p>

      <p className="text-lg text-slate-600 leading-relaxed mb-8">
        Here's what APAAR is, why it matters, and how your institution can manage it digitally.
      </p>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-8">
        <div className="bg-white rounded-xl p-6 text-center border">
          <div className="text-3xl font-extrabold text-indigo-600">26.35 Cr+</div>
          <div className="text-sm text-slate-500">APAAR IDs generated across India</div>
        </div>
        <div className="bg-white rounded-xl p-6 text-center border">
          <div className="text-3xl font-extrabold text-emerald-600">12</div>
          <div className="text-sm text-slate-500">Digit unique ID per student</div>
        </div>
        <div className="bg-white rounded-xl p-6 text-center border">
          <div className="text-3xl font-extrabold text-amber-600">7</div>
          <div className="text-sm text-slate-500">Years of credit validity</div>
        </div>
      </div>

      <h2 className="text-2xl font-bold text-slate-900 mb-6">What is APAAR and Why Does It Matter?</h2>

      {/* Key Features */}
      {keyFeatures.map((item, i) => (
        <div key={i} className={`${item.color} rounded-2xl p-6 mb-6 border`}>
          <div className="flex items-start gap-4">
            <div className="bg-white rounded-xl p-3 shadow-sm flex-shrink-0 mt-1">
              {item.icon}
            </div>
            <div className="flex-1">
              <h3 className="text-xl font-bold text-slate-900 mb-2">{item.title}</h3>
              <p className="text-slate-600 leading-relaxed text-sm mb-4">{item.description}</p>
              <div className="bg-white/60 rounded-xl p-3 border border-amber-200">
                <p className="text-sm font-semibold text-amber-600">📊 {item.stat}</p>
              </div>
            </div>
          </div>
        </div>
      ))}

      {/* Benefits Section */}
      <div className="mt-8 bg-white rounded-2xl border border-slate-200 p-6 sm:p-8">
        <h3 className="text-xl font-bold text-slate-900 mb-4">Benefits of APAAR for Students and Institutions</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {benefits.map((item, i) => (
            <div key={i} className="bg-indigo-50 rounded-xl p-4 border border-indigo-200">
              <div className="flex items-start gap-3">
                <div className="bg-white rounded-lg p-2 flex-shrink-0">
                  {item.icon}
                </div>
                <div>
                  <h4 className="font-bold text-slate-900 text-sm">{item.title}</h4>
                  <p className="text-xs text-slate-600">{item.description}</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Management Process */}
      <div className="mt-8 bg-white rounded-2xl border border-slate-200 p-6 sm:p-8">
        <h3 className="text-xl font-bold text-slate-900 mb-4">How Institutions Manage APAAR IDs Digitally</h3>
        <div className="space-y-4">
          {managementSteps.map((item, i) => (
            <div key={i} className="bg-blue-50 rounded-xl p-4 border border-blue-200">
              <div className="flex items-start gap-3">
                <span className="bg-blue-600 text-white rounded-full w-6 h-6 flex items-center justify-center text-xs font-bold flex-shrink-0 mt-0.5">
                  {i + 1}
                </span>
                <div>
                  <h4 className="font-bold text-slate-900 text-sm">{item.title}</h4>
                  <p className="text-sm text-slate-600 mt-1">{item.description}</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Consent Process */}
      <div className="mt-8 bg-amber-50 rounded-2xl p-6 border border-amber-200">
        <h3 className="text-lg font-bold text-amber-900 mb-3">The Consent Process: What Institutions Must Know</h3>
        <p className="text-amber-800 leading-relaxed mb-3">
          APAAR enrolment is voluntary and consent-based [citation:5]. For minor students, schools must obtain parental consent before creating an APAAR ID [citation:3][citation:7].
        </p>
        <ul className="text-amber-800 leading-relaxed space-y-2">
          <li className="flex items-start gap-2">• <span>Schools must provide physical consent forms to parents</span></li>
          <li className="flex items-start gap-2">• <span>Parents should understand the purpose, benefits, and data usage before signing</span></li>
          <li className="flex items-start gap-2">• <span>Parents must have the option to refuse consent before enrolment [citation:7]</span></li>
          <li className="flex items-start gap-2">• <span>The consent process must comply with the Digital Personal Data Protection (DPDP) Act, 2023 [citation:7][citation:8]</span></li>
          <li className="flex items-start gap-2">• <span>Schools should conduct parent-teacher meetings to explain APAAR and address concerns [citation:6][citation:10]</span></li>
        </ul>
        <p className="text-amber-800 leading-relaxed mt-3">
          <strong>Important:</strong> Institutions must ensure that APAAR IDs are created only for genuinely enrolled students with accurate data. Creating dummy or incorrect IDs can lead to disciplinary action [citation:4].
        </p>
      </div>

      {/* Why Truyon Section */}
      <div className="mt-8 bg-indigo-50 rounded-2xl p-6 border border-indigo-200">
        <h3 className="text-lg font-bold text-indigo-900 mb-3">How Truyon Simplifies APAAR ID Management</h3>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <div className="bg-white rounded-xl p-4 border border-indigo-200 text-center">
            <div className="text-2xl mb-1">📋</div>
            <h4 className="font-bold text-slate-900 text-sm">Automated Data Verification</h4>
            <p className="text-xs text-slate-600">Verify student details against UDISE+ and Aadhaar</p>
          </div>
          <div className="bg-white rounded-xl p-4 border border-indigo-200 text-center">
            <div className="text-2xl mb-1">🔄</div>
            <h4 className="font-bold text-slate-900 text-sm">Consent Form Management</h4>
            <p className="text-xs text-slate-600">Track consent forms and parent permissions</p>
          </div>
          <div className="bg-white rounded-xl p-4 border border-indigo-200 text-center">
            <div className="text-2xl mb-1">🔗</div>
            <h4 className="font-bold text-slate-900 text-sm">NAD/ABC Integration</h4>
            <p className="text-xs text-slate-600">Seamless integration with NAD portal</p>
          </div>
          <div className="bg-white rounded-xl p-4 border border-indigo-200 text-center">
            <div className="text-2xl mb-1">📊</div>
            <h4 className="font-bold text-slate-900 text-sm">Real-Time Upload</h4>
            <p className="text-xs text-slate-600">Upload academic records to ABC instantly</p>
          </div>
          <div className="bg-white rounded-xl p-4 border border-indigo-200 text-center">
            <div className="text-2xl mb-1">📱</div>
            <h4 className="font-bold text-slate-900 text-sm">DigiLocker Integration</h4>
            <p className="text-xs text-slate-600">Push APAAR IDs directly to DigiLocker</p>
          </div>
          <div className="bg-white rounded-xl p-4 border border-indigo-200 text-center">
            <div className="text-2xl mb-1">🔒</div>
            <h4 className="font-bold text-slate-900 text-sm">Data Security</h4>
            <p className="text-xs text-slate-600">GDPR and DPDP Act compliant</p>
          </div>
        </div>
      </div>

      {/* Conclusion */}
      <div className="mt-8 bg-indigo-50 rounded-2xl p-6 border border-indigo-200">
        <h3 className="text-lg font-bold text-indigo-900 mb-2">Manage APAAR IDs Easily with Truyon</h3>
        <p className="text-indigo-800 leading-relaxed">
          APAAR ID is becoming a mandatory requirement for all educational institutions in India. Managing APAAR IDs manually—verifying student data, collecting consent forms, generating IDs, and integrating with NAD—is time-consuming and error-prone.
        </p>
        <p className="text-indigo-800 leading-relaxed mt-3">
          Truyon streamlines the entire APAAR management process. Automated data verification, consent tracking, NAD integration, and real-time record uploads—all in one platform.
        </p>
        <p className="text-indigo-800 leading-relaxed mt-3">
          <strong>Truyon for APAAR ID management.</strong> Stay compliant. Save time. Keep student records secure.
        </p>
      </div>
    </>
  );
};

export default APAARID;